import { useEffect, useState, useCallback } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { euroPrecise } from "../lib/constants";
import {
    Search,
    MapPin,
    Globe,
    Plus,
    Check,
    Tag,
    Network,
    X,
    Filter,
    Star,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Dialog "Scopri Fornitori" — esplora il pool globale dei fornitori
 * condivisi tra tutti gli utenti della piattaforma. L'utente può filtrare per
 * regione, città, prezzo medio, e selezionare i fornitori da aggiungere al
 * proprio portale. Inoltre può marcare i preferiti (★) che saranno gli
 * UNICI considerati dal sistema intelligente di splitting ordini.
 */
export default function SupplierDiscoverDialog({ open, onOpenChange, onChanged }) {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        q: "",
        region: "",
        city: "",
        max_avg_price: "",
        only_selected: false,
        only_preferred: false,
    });
    const [meta, setMeta] = useState({ regions: [], cities: [] });
    const [busyId, setBusyId] = useState(null);
    const [selectedCount, setSelectedCount] = useState(0);
    const [preferredCount, setPreferredCount] = useState(0);

    const fetchMeta = useCallback(async () => {
        try {
            const { data } = await api.get("/suppliers/regions");
            setMeta(data);
        } catch {
            // ignore
        }
    }, []);

    const fetchResults = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.q.trim()) params.set("q", filters.q.trim());
            if (filters.region) params.set("region", filters.region);
            if (filters.city) params.set("city", filters.city);
            if (filters.max_avg_price) params.set("max_avg_price", filters.max_avg_price);
            if (filters.only_selected) params.set("only_selected", "true");
            if (filters.only_preferred) params.set("only_preferred", "true");
            const { data } = await api.get(`/suppliers/discover?${params.toString()}`);
            setResults(data.results || []);
            setSelectedCount(data.selected_count || 0);
            setPreferredCount(data.preferred_count || 0);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        if (open) {
            fetchMeta();
            fetchResults();
        }
    }, [open, fetchMeta, fetchResults]);

    const toggleSelect = async (sup) => {
        setBusyId(sup.id);
        try {
            if (sup.is_selected) {
                await api.delete(`/suppliers/${sup.id}/select`);
                toast.success(`${sup.name} rimosso dal tuo portale`);
            } else {
                await api.post(`/suppliers/${sup.id}/select`);
                toast.success(`${sup.name} aggiunto al tuo portale`);
            }
            setResults((prev) =>
                prev.map((s) =>
                    s.id === sup.id
                        ? { ...s, is_selected: !sup.is_selected, is_preferred: sup.is_selected ? false : s.is_preferred }
                        : s,
                ),
            );
            setSelectedCount((c) => c + (sup.is_selected ? -1 : 1));
            if (sup.is_selected && sup.is_preferred) setPreferredCount((c) => Math.max(0, c - 1));
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const togglePrefer = async (sup) => {
        setBusyId(sup.id);
        try {
            if (sup.is_preferred) {
                await api.delete(`/suppliers/${sup.id}/prefer`);
                toast.success(`${sup.name} non più preferito`);
            } else {
                await api.post(`/suppliers/${sup.id}/prefer`);
                toast.success(`★ ${sup.name} aggiunto ai preferiti`);
            }
            setResults((prev) =>
                prev.map((s) =>
                    s.id === sup.id
                        ? { ...s, is_preferred: !sup.is_preferred, is_selected: true }
                        : s,
                ),
            );
            setPreferredCount((c) => c + (sup.is_preferred ? -1 : 1));
            if (!sup.is_selected) setSelectedCount((c) => c + 1);
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const resetFilters = () => {
        setFilters({ q: "", region: "", city: "", max_avg_price: "", only_selected: false, only_preferred: false });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-4xl bg-white p-0 overflow-hidden max-h-[90vh] flex flex-col"
                data-testid="discover-suppliers-dialog"
            >
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-brand-line">
                    <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-brand-gold/15 text-brand-goldDark flex items-center justify-center shrink-0">
                            <Network size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <DialogTitle className="font-display text-2xl text-brand-ink">
                                Scopri fornitori della rete
                            </DialogTitle>
                            <DialogDescription className="text-sm text-brand-muted mt-1">
                                Tutti i fornitori inseriti dagli utenti RENOVA360 sono qui. Filtra,
                                confronta il prezzo medio e aggiungi al tuo portale quelli che vuoi
                                usare. <strong>Fatture e listini privati restano sempre tuoi.</strong>
                            </DialogDescription>
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3 text-[11px] uppercase tracking-wider text-brand-muted">
                        <div className="inline-flex items-center gap-1.5" data-testid="discover-selected-count">
                            <Tag size={11} className="text-brand-gold" />
                            Nel portale: <strong className="text-brand-navy">{selectedCount}</strong>
                        </div>
                        <div className="inline-flex items-center gap-1.5" data-testid="discover-preferred-count">
                            <Star size={11} className="text-amber-500 fill-amber-400" />
                            Preferiti per ordini: <strong className="text-amber-700">{preferredCount}</strong>
                        </div>
                    </div>
                </DialogHeader>

                {/* FILTERS */}
                <div className="px-6 py-4 border-b border-brand-line bg-brand-cream/40">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        <div className="lg:col-span-2 relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                            <input
                                type="text"
                                value={filters.q}
                                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                                placeholder="Cerca per nome, città o regione…"
                                data-testid="discover-search"
                                className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                            />
                        </div>
                        <select
                            value={filters.region}
                            onChange={(e) => setFilters({ ...filters, region: e.target.value })}
                            data-testid="discover-region"
                            className="px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        >
                            <option value="">Tutte le regioni</option>
                            {meta.regions.map((r) => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                        <select
                            value={filters.city}
                            onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                            data-testid="discover-city"
                            className="px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        >
                            <option value="">Tutte le città</option>
                            {meta.cities.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={filters.max_avg_price}
                            onChange={(e) => setFilters({ ...filters, max_avg_price: e.target.value })}
                            placeholder="Prezzo medio max €"
                            data-testid="discover-maxprice"
                            className="px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        />
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-4 flex-wrap">
                            <label className="inline-flex items-center gap-2 text-[12px] text-brand-ink cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filters.only_selected}
                                    onChange={(e) => setFilters({ ...filters, only_selected: e.target.checked, only_preferred: false })}
                                    data-testid="discover-only-selected"
                                    className="h-3.5 w-3.5 accent-brand-navy"
                                />
                                Solo nel mio portale
                            </label>
                            <label className="inline-flex items-center gap-2 text-[12px] text-brand-ink cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filters.only_preferred}
                                    onChange={(e) => setFilters({ ...filters, only_preferred: e.target.checked, only_selected: false })}
                                    data-testid="discover-only-preferred"
                                    className="h-3.5 w-3.5 accent-amber-500"
                                />
                                <Star size={12} className="text-amber-500 fill-amber-400" /> Solo preferiti
                            </label>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={resetFilters}
                                data-testid="discover-reset"
                                className="text-[12px] text-brand-muted hover:text-brand-ink inline-flex items-center gap-1"
                            >
                                <X size={12} /> Reset filtri
                            </button>
                            <button
                                onClick={fetchResults}
                                data-testid="discover-apply"
                                className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white px-4 py-1.5 text-[12px] font-bold hover:bg-brand-navyLight transition"
                            >
                                <Filter size={12} /> Applica
                            </button>
                        </div>
                    </div>
                </div>

                {/* RESULTS */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading ? (
                        <div className="py-12 text-center text-sm text-brand-muted">Caricamento…</div>
                    ) : results.length === 0 ? (
                        <div className="py-12 text-center">
                            <Network size={32} className="mx-auto text-brand-muted mb-3" strokeWidth={1.4} />
                            <div className="font-display text-lg text-brand-ink">Nessun fornitore trovato</div>
                            <div className="text-sm text-brand-muted mt-1">
                                Prova a modificare i filtri o aggiungi un nuovo fornitore dal pulsante "Nuovo fornitore".
                            </div>
                        </div>
                    ) : (
                        <div
                            className="grid grid-cols-1 md:grid-cols-2 gap-3"
                            data-testid="discover-results"
                        >
                            {results.map((s) => (
                                <SupplierResultCard
                                    key={s.id}
                                    s={s}
                                    busy={busyId === s.id}
                                    onToggle={() => toggleSelect(s)}
                                    onTogglePrefer={() => togglePrefer(s)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-brand-line text-[11px] text-brand-muted text-center">
                    {results.length} risultat{results.length === 1 ? "o" : "i"} mostrat{results.length === 1 ? "o" : "i"}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function SupplierResultCard({ s, busy, onToggle, onTogglePrefer }) {
    return (
        <div
            className={`rounded-xl border p-4 transition relative ${
                s.is_preferred
                    ? "border-amber-400 bg-amber-50/50 ring-1 ring-amber-300"
                    : s.is_selected
                    ? "border-emerald-300 bg-emerald-50/40"
                    : "border-brand-line bg-paper hover:border-brand-gold/50"
            }`}
            data-testid={`discover-card-${s.id}`}
        >
            <button
                type="button"
                onClick={onTogglePrefer}
                disabled={busy}
                title={s.is_preferred ? "Togli dai preferiti" : "Aggiungi ai preferiti (usati dal sistema di splitting ordini)"}
                data-testid={`discover-prefer-${s.id}`}
                className="absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center transition disabled:opacity-50 hover:bg-amber-100"
            >
                <Star
                    size={20}
                    strokeWidth={2}
                    className={
                        s.is_preferred
                            ? "text-amber-500 fill-amber-400"
                            : "text-brand-muted hover:text-amber-500"
                    }
                />
            </button>
            <div className="flex items-start justify-between gap-3 pr-10">
                <div className="flex-1 min-w-0">
                    <div className="font-display text-lg text-brand-ink leading-tight truncate">
                        {s.name}
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-[12px] text-brand-muted flex-wrap">
                        {s.category && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold/15 text-brand-goldDark px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                                {s.category}
                            </span>
                        )}
                        {(s.city || s.region) && (
                            <span className="inline-flex items-center gap-1">
                                <MapPin size={11} />
                                {[s.city, s.region].filter(Boolean).join(", ")}
                            </span>
                        )}
                        {s.website && (
                            <a
                                href={s.website.startsWith("http") ? s.website : `https://${s.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 hover:text-brand-ink"
                            >
                                <Globe size={11} /> Sito
                            </a>
                        )}
                    </div>
                </div>
                {s.average_price !== null && s.average_price !== undefined && (
                    <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-wider text-brand-muted">
                            Prezzo medio rete
                        </div>
                        <div className="font-display text-xl text-brand-gold leading-none mt-0.5">
                            {euroPrecise(s.average_price)}
                        </div>
                    </div>
                )}
            </div>
            {s.notes && (
                <div className="mt-2 text-[12px] text-brand-muted italic line-clamp-2">{s.notes}</div>
            )}
            <button
                onClick={onToggle}
                disabled={busy}
                data-testid={`discover-toggle-${s.id}`}
                className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-bold transition disabled:opacity-60 ${
                    s.is_selected
                        ? "bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-200"
                        : "bg-brand-navy text-white hover:bg-brand-navyLight"
                }`}
            >
                {busy ? (
                    "Attendi…"
                ) : s.is_selected ? (
                    <>
                        <Check size={13} /> Nel portale {s.is_preferred && "★"}
                    </>
                ) : (
                    <>
                        <Plus size={13} /> Aggiungi al portale
                    </>
                )}
            </button>
        </div>
    );
}
